/**
 * Search Overlay — port 1:1 từ Joomla shop.site_search_ajax.php +
 * browse/includes/site_search.tpl.php + searchSite() (vm_SiteSearchInText.tpl.php).
 *
 * Flow:
 *  1. Focus vào search input → hiện overlay với popular keywords (state: popular)
 *  2. Nhập keyword (debounce 300ms) → fetch API suggestions → hiện kết quả (state: results)
 *  3. Ô rỗng / chỉ khoảng trắng → quay về popular (reset filter 種類/メーカー về mặc định)
 *  4. Close: click ×, click "検索結果を閉じる", click nền, Esc
 *  5. Click result → navigate /collections/{handle} (handle API ưu tiên, thiếu thì
 *     handleize(tên)); collection không tồn tại → 404 Shopify, KHÔNG về trang search
 *  6. Select maker green bar (siteSearchMaker) → onchange re-search
 *  7. Kết quả: 検索結果：N件 + 絞り込み検索 (種類 checkbox + メーカー radio) +
 *     kết quả group theo maker × 型番kind (title "EPSON / インク型番名") — port
 *     site_search.tpl.php. Result maker radio (categoryCheckOption) override green bar.
 *  8. Submit/Enter → chặn (preventDefault) + mở overlay (site cũ KHÔNG có trang
 *     kết quả keyword)
 *
 * API: GET /apps/ink/api/v1/search/suggestions?q={kw}&maker={id}&checked={csv}
 *   Response: { data: { results: [{category_id, category_name, category_name_text,
 *     symbol_mark, category_kind, category_maker, shopify_collection_handle}], total } }
 */

(function () {
  'use strict';

  const OVERLAY_ID = 'ink-search-overlay';
  const INPUT_ATTR = 'data-ink-search-input';
  const DEBOUNCE_MS = 300;

  // Trần request/phút phía client (vm_SiteSearchInText.tpl.php:53) — search chạy
  // từ 1 ký tự nên đây là cơ chế duy nhất chặn gõ liên tục làm ngập API.
  const MAX_REQUESTS_PER_MINUTE = 40;
  const RATE_WINDOW_MS = 60000;

  // Category「レンタルPLUS株式会社様専用販売ページ」bị loại khỏi kết quả +
  // không tính vào 件数 (port shop.site_search_ajax.php:54-58).
  const RENTAL_PLUS_NAME = 'レンタルPLUS株式会社様専用販売ページ';

  // makerListJPSearch (ps_product_category.cfg.php) — maker_id (đã remap) → tên hiển thị.
  const MAKER_LABELS = {
    1: 'EPSON',
    2: 'Canon',
    5: 'brother',
    6: 'hp',
    7: 'RICOH',
    8: 'DELL',
    9: 'LEXMARK',
    10: 'EMシステムズ',
    11: 'その他',
  };

  // kind_search (ps_product_category.cfg.php) — category_kind → hậu tố tiêu đề nhóm.
  const KIND_TITLES = {
    0: '',
    1: ' / インク型番名',
    2: ' / トナー型番名',
    3: ' / プリンター型番名',
    4: ' / プリンター型番名',
    5: ' / その他',
    6: ' / その他',
    99: ' / その他',
  };

  class SearchOverlay {
    constructor(inputEl, apiBase) {
      this.input = inputEl;
      this.apiBase = apiBase; // "/apps/ink/api/v1"
      this.overlay = document.getElementById(OVERLAY_ID);
      // Select maker green bar (siteSearchMaker) — value = category_maker ID.
      this.makerSelect = document.querySelector('[data-ink-search-maker]');
      this.debounceTimer = null;
      this.lastQuery = '';
      this.abortController = null;

      // Filter state trong khối kết quả (port categoryCheckOption + checked).
      // resultMaker=null → dùng green bar; set (kể cả '0') → override.
      this.resultMaker = null;
      this.checkedKinds = [];

      // Bộ đếm rate limit (vm_SiteSearchInText.tpl.php:51-69)
      this.requestCount = 0;
      this.rateWindowStart = Date.now();

      if (!this.overlay || !this.input) return;

      this._bindEvents();
    }

    // --- Public methods ---

    show() {
      // Panel absolute ngay dưới ô search (CSS top-full) — KHÔNG backdrop, KHÔNG
      // khoá scroll body (giống design) → không gây xê dịch/phình trang.
      this.overlay.classList.remove('hidden');
    }

    hide() {
      this.overlay.classList.add('hidden');
      this._cancelFetch();
    }

    // --- Private: event binding ---

    _bindEvents() {
      // Focus: hiện overlay với popular state
      this.input.addEventListener('focus', () => {
        const q = this.input.value.trim();
        this.show();
        if (q !== '') {
          this._triggerSearch(q);
        } else {
          this._showPopular();
        }
      });

      // Input: debounce search. Site cũ KHÔNG có ngưỡng số ký tự tối thiểu —
      // keyword 1 ký tự cũng search (vm_SiteSearchInText.tpl.php:122-126).
      this.input.addEventListener('input', () => {
        const q = this.input.value.trim();
        clearTimeout(this.debounceTimer);
        if (q === '') {
          this._showPopular();
          return;
        }
        this.debounceTimer = setTimeout(() => this._triggerSearch(q), DEBOUNCE_MS);
      });

      // Select maker green bar → re-search ngay (mirror site cũ onchange="searchSite()")
      if (this.makerSelect) {
        this.makerSelect.addEventListener('change', () => this._reSearchFromFilter());
      }

      // 種類 checkbox (typeSiteSearch) → cập nhật checkedKinds + re-search
      this.overlay.querySelectorAll('[data-ink-search-kind]').forEach((cb) => {
        cb.addEventListener('change', () => {
          this.checkedKinds = Array.from(
            this.overlay.querySelectorAll('[data-ink-search-kind]:checked'),
          ).map((el) => el.value);
          this._reSearchFromFilter();
        });
      });

      // メーカー radio kết quả (siteSearchMaker2) → override green bar + re-search
      this.overlay.querySelectorAll('[data-ink-search-maker2]').forEach((rb) => {
        rb.addEventListener('change', () => {
          if (rb.checked) this.resultMaker = rb.value;
          this._reSearchFromFilter();
        });
      });

      // Chặn submit (Enter / nút 🔍) → mở overlay thay vì rơi vào /search
      // boilerplate — site cũ KHÔNG có trang kết quả keyword (form comment out)
      const form = this.input.closest('[data-ink-search-form]') || this.input.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this._reSearchFromFilter();
        });
      }

      // Esc key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
      });

      // Click ra ngoài overlay (và không phải ô search/maker) → đóng (design không có backdrop)
      document.addEventListener('click', (e) => {
        if (this.overlay.classList.contains('hidden')) return;
        if (this.overlay.contains(e.target)) return;
        if (this.input.contains(e.target)) return;
        if (this.makerSelect && this.makerSelect.contains(e.target)) return;
        const form = this.input.closest('[data-ink-search-form]');
        if (form && form.contains(e.target)) return;
        this.hide();
      });

      // × button
      const closeX = document.getElementById('ink-search-close-x');
      if (closeX) closeX.addEventListener('click', () => this.hide());

      // "検索結果を閉じる" button
      const closeBtn = document.getElementById('ink-search-close-btn');
      if (closeBtn) closeBtn.addEventListener('click', () => this.hide());
    }

    // Re-search theo state filter hiện tại (nếu keyword đủ dài; else về popular)
    _reSearchFromFilter() {
      const q = this.input.value.trim();
      this.show();
      if (q !== '') {
        this._triggerSearch(q, true);
      } else {
        this._showPopular();
      }
    }

    // --- Private: state management ---

    _showPopular() {
      // Reset filter về mặc định (site cũ: default view không có Refine box)
      this._resetFilter();
      this._setHeading('よく検索される商品・プリンター');
      this._toggleEl('ink-search-popular', true);
      this._toggleEl('ink-search-results', false);
      this._toggleEl('ink-search-loading', false);
    }

    _resetFilter() {
      this.resultMaker = null;
      this.checkedKinds = [];
      this.overlay
        .querySelectorAll('[data-ink-search-kind]:checked, [data-ink-search-maker2]:checked')
        .forEach((el) => {
          el.checked = false;
        });
    }

    _showLoading() {
      this._setHeading('検索中...');
      this._toggleEl('ink-search-popular', false);
      this._toggleEl('ink-search-results', false);
      this._toggleEl('ink-search-loading', true);
    }

    _showResults(results) {
      // Loại レンタルPLUS + không tính vào 件数 (port shop.site_search_ajax.php)
      const items = results.filter(
        (r) => r.category_name !== RENTAL_PLUS_NAME && r.category_name_text !== RENTAL_PLUS_NAME,
      );

      this._renderGroups(items);

      const countEl = document.getElementById('ink-search-count');
      if (countEl) countEl.textContent = String(items.length);

      const noResults = document.getElementById('ink-search-no-results');
      if (noResults) noResults.classList.toggle('hidden', items.length !== 0);

      this._setHeading('検索結果');
      this._toggleEl('ink-search-popular', false);
      this._toggleEl('ink-search-loading', false);
      this._toggleEl('ink-search-results', true);
    }

    // --- Private: render groups (maker × kind) ---

    // Remap category_maker → key makerListJPSearch (port shop.site_search_ajax.php:64-83,
    // giữ nguyên chuỗi if tuần tự — DELL(9)/LEXMARK(8) đều gộp về 9=LEXMARK như site cũ).
    _remapMaker(rawId) {
      let id = parseInt(rawId, 10) || 0;
      if ((id < 1 || id > 9) && id !== 15) id = 11;
      if (id === 9) id = 8;
      if (id === 8) id = 9;
      if (id === 15) id = 11;
      return id;
    }

    _renderGroups(items) {
      const wrap = document.getElementById('ink-search-groups');
      if (!wrap) return;

      // Group: { makerId: { kindTitle: [items...] } } — insertion order = thứ tự
      // row (API đã ORDER BY kind,maker,name) → inner theo kind; outer sort maker asc.
      const groups = {};
      items.forEach((item) => {
        const maker = this._remapMaker(item.category_maker);
        const kindTitle = KIND_TITLES[item.category_kind] || '';
        if (!groups[maker]) groups[maker] = {};
        if (!groups[maker][kindTitle]) groups[maker][kindTitle] = [];
        groups[maker][kindTitle].push(item);
      });

      const makerKeys = Object.keys(groups)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b);

      let html = '';
      makerKeys.forEach((maker) => {
        const makerLabel = MAKER_LABELS[maker] || '';
        Object.keys(groups[maker]).forEach((kindTitle) => {
          const title = this._esc(makerLabel + kindTitle);
          // Cell = tile lưới GIỐNG view mặc định (popular): grid-cols-2 lg:grid-cols-3,
          // bg xám xen kẽ gray-50/gray-100, chữ ink-blue bold, hover opacity.
          // View kết quả style y hệt view mặc định, chỉ thêm filter.
          const cells = groups[maker][kindTitle]
            .map((item, idx) => {
              const bg = idx % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100';
              return `<a href="${this._collectionUrl(item)}" class="${bg} px-2 py-4 no-underline hover:opacity-80 transition-opacity lg:py-5">${this._esc(this._displayName(item))}</a>`;
            })
            .join('');
          html += `<section>
              <h3 class="mb-4 border-l-4 border-gray-400 pl-3 text-lg font-bold lg:text-xl">${title}</h3>
              <div class="grid grid-cols-2 text-center text-base font-bold text-ink-blue lg:grid-cols-3 lg:text-xl">${cells}</div>
            </section>`;
        });
      });

      wrap.innerHTML = html;
    }

    // URL kết quả — LUÔN trỏ /collections/{handle}.
    // Category chưa sync handle → derive handleize(tên) như chip 適合プリンター:
    // collection có thật thì vào được, KHÔNG tồn tại → Shopify trả 404 native.
    // KHÔNG fallback /search?q= — sẽ ra trang kết quả search lạc đề.
    _collectionUrl(item) {
      const handle = item.shopify_collection_handle || this._handleize(item.category_name);
      // Handle rỗng (tên toàn ký tự đặc biệt) → dùng tên raw để chắc chắn 404,
      // tránh `/collections/` trần (Shopify render danh sách toàn bộ collection).
      const safe = handle || encodeURIComponent(item.category_name || 'not-found');
      return `/collections/${safe}`;
    }

    // Handleize giống Liquid filter: lowercase, ký tự không phải chữ/số → '-', trim '-'.
    // Dùng \p{L}\p{N} để giữ tên tiếng Nhật (Shopify handle cho phép Unicode).
    _handleize(name) {
      return String(name || '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '');
    }

    // Tên hiển thị — port shop.site_search_ajax.php:59-63:
    // category_name_text ưu tiên; else symbol_mark → "name（symbol）"; else name.
    _displayName(item) {
      if (item.category_name_text) return item.category_name_text;
      if (item.symbol_mark) return `${item.category_name}（${item.symbol_mark}）`;
      return item.category_name;
    }

    // --- Private: API fetch ---

    _triggerSearch(query, force = false) {
      const maker = this._effectiveMaker();
      const checkedCsv = this.checkedKinds.slice().sort().join(',');
      const cacheKey = `${maker}|${checkedCsv}|${query}`;
      // force = do đổi filter → bỏ qua cache (cùng key nếu spam vẫn dedup)
      if (!force && cacheKey === this.lastQuery) return;

      // Chỉ trừ budget khi thật sự phát request (lượt bị dedup không tính)
      if (!this._consumeRateBudget()) return;
      this.lastQuery = cacheKey;

      this._cancelFetch();
      this._showLoading();

      this.abortController = new AbortController();
      let url = `${this.apiBase}/search/suggestions?q=${encodeURIComponent(query)}`;
      if (maker !== '0') url += `&maker=${encodeURIComponent(maker)}`;
      if (checkedCsv) url += `&checked=${encodeURIComponent(checkedCsv)}`;

      // ngrok-skip-browser-warning: bypass trang cảnh báo ngrok free khi App Proxy
      // trỏ ngrok dev (ERR_NGROK_6024 trả HTML → JSON parse fail). Giống collection.js.
      fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json', 'ngrok-skip-browser-warning': '1' },
      })
        .then((res) => res.json())
        .then((json) => {
          const results = json?.data?.results || [];
          this._showResults(results);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            // Fallback: popular state khi API lỗi (log để không nuốt lỗi im lặng)
            console.warn('[search-overlay] suggestions fetch failed', err);
            this._showPopular();
          }
        });
    }

    // Trần MAX_REQUESTS_PER_MINUTE request/phút (vm_SiteSearchInText.tpl.php:56-69):
    // vượt trần thì bỏ qua lượt search, giữ nguyên kết quả đang hiện.
    _consumeRateBudget() {
      const now = Date.now();
      if (now - this.rateWindowStart > RATE_WINDOW_MS) {
        this.rateWindowStart = now;
        this.requestCount = 0;
      }
      if (this.requestCount >= MAX_REQUESTS_PER_MINUTE) {
        console.warn('1分間に40回のリクエストの上限に達しました。しばらくお待ちください。');
        return false;
      }
      this.requestCount += 1;
      return true;
    }

    _cancelFetch() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    // --- Utils ---

    // Maker gửi lên API: result radio (categoryCheckOption) override green bar khi đã chọn.
    _effectiveMaker() {
      if (this.resultMaker !== null) return this.resultMaker;
      return (this.makerSelect && this.makerSelect.value) || '0';
    }

    _toggleEl(id, visible) {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.toggle('hidden', !visible);
    }

    _setHeading(text) {
      const el = document.getElementById('ink-search-overlay-heading');
      if (el) el.textContent = text;
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  // --- Init ---

  function init() {
    const inputEl = document.querySelector(`[${INPUT_ATTR}]`);
    if (!inputEl) return;

    // App Proxy base (architecture)
    const apiBase = inputEl.dataset.inkSearchApi || '/apps/ink/api/v1';
    new SearchOverlay(inputEl, apiBase);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
